import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import Button from '~/components/Button';
import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';
import Notifications from '~/components/Notifications';
import useIsMobile from '~/hooks/useIsMobile';
import type { Publication } from '~/interfaces/publication';
import { SHADOW_OPTIONS } from '~/utils/designOptions';

import MobileDropdown from './MobileDropdown';

interface Props {
  hideDropdown?: boolean;
  ctaText: string;
  showActions: boolean;
  publication: Publication;
}

const themeFallback = (theme1: string, theme2: string) => {
  return theme1 || theme2;
};

const MenuActions = ({ ctaText, showActions, hideDropdown = false, publication }: Props) => {
  const { webTheme: theme } = useWebThemeContext();
  const notificationsEnabled = publication.web_template?.notifications_enabled;

  const isMobile = useIsMobile();

  const {
    navbar_button_primary_background_color,
    color_primary,
    navbar_button_primary_text_color,
    navbar_button_primary_border_color,
    navbar_button_secondary_border_color,
    color_text_on_primary,
    navbar_button_secondary_background_color,
    color_secondary,
    navbar_button_secondary_text_color,
    color_text_on_secondary,
    color_border,
    navbar_button_primary_shadow,
    navbar_button_secondary_shadow,
    navbar_login_button_desktop_enabled,
    font_button,
  } = theme;

  const bgColor = themeFallback(navbar_button_primary_background_color, color_primary);
  const textColor = themeFallback(navbar_button_primary_text_color, color_text_on_primary);
  const secondaryBgColor = themeFallback(navbar_button_secondary_background_color, color_secondary);
  const secondaryTextColor = themeFallback(navbar_button_secondary_text_color, color_text_on_secondary);
  const primaryBorderColor = themeFallback(navbar_button_primary_border_color, color_primary);
  const secondaryBorderColor = themeFallback(navbar_button_secondary_border_color, color_border);

  const primaryShadowClass = SHADOW_OPTIONS[navbar_button_primary_shadow || 'none'];

  const secondaryShadowClass = SHADOW_OPTIONS[navbar_button_secondary_shadow || 'none'];

  const { t } = useTranslation();
  const { handleOpenModal } = useGlobalModals();

  const handleLogin = () => {
    handleOpenModal('login');
  };

  return (
    <div className="flex items-center space-x-2">
      {showActions ? (
        <div className="flex items-center space-x-2">
          {navbar_login_button_desktop_enabled && (
            <Button
              style={{
                background: secondaryBgColor,
                border: `1px solid ${secondaryBorderColor}`,
                color: secondaryTextColor,
                fontFamily: font_button,
              }}
              variant="ghost"
              onClick={handleLogin}
              className={cx('!text-sm !font-semibold', secondaryShadowClass)}
            >
              {t('shared.cta.login')}
            </Button>
          )}
          <Button
            linkProps={{ to: '/subscribe', prefetch: 'intent' }}
            style={{
              background: bgColor,
              border: `1px solid ${primaryBorderColor}`,
              color: textColor,
              fontFamily: font_button,
            }}
            variant="primary"
            className={cx('whitespace-nowrap !text-sm !font-semibold', primaryShadowClass)}
          >
            {ctaText}
          </Button>
          {notificationsEnabled && <Notifications />}
        </div>
      ) : (
        <div className="flex items-center">{notificationsEnabled && <Notifications />}</div>
      )}
      {!hideDropdown && (
        <MobileDropdown dropdownDirection={isMobile ? 'center' : 'right'} theme={theme} publication={publication} />
      )}
    </div>
  );
};

export default MenuActions;
